import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {  Admin, Jisa } from "type/type";
import styled from "styled-components";
import { createAdmin, encryptSHA256, getAdminDuplicateCheck, getJisaAll, maxLengthCheck } from "api/api";
import { useForm } from "react-hook-form";
import hubLayout from 'styles/css/hubLayout.module.css';
import { useRecoilState } from "recoil";
import { isDarkAtom } from "styles/atoms";
import { FiInfo } from "react-icons/fi";
import { AdminLoginInfoContext } from "pages/Layout";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
    &:not(:first-of-type) {
      margin-bottom: 1rem;
      background-color: ${prop => prop.theme.layer3BgColor};
      border-radius: 0.5rem;
    }
  }
  details {
    background-color: ${({ theme }) => theme.newTheme.layer.layer2};
    border-radius: 0.5rem;
    summary {
      background-color: ${({ theme }) => theme.newTheme.root.secondary};
      padding: 0.25rem 1rem;
      border-radius: 0.5rem;
      &::-webkit-details-marker { /* Safari 브라우저용 사용자 정의 스타일 */
        display: none;
      }
      &::marker {
        font-size: 1.5rem;
      }
    }
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  label, input[name=text], select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  input[name=text], select, textarea {
    border-radius: 0.25rem;
    font-family: var(--font-pretendard-medium);
    &:focus-visible {
      outline: none;
    }
  }
  input[name=text]:disabled {
    background-color: ${prop => prop.theme.readOnlyColor};
    color: ${prop => prop.theme.rootTextColor};
    cursor: auto;
  }
  > dd {
    flex: none !important;
  }

  details {
    cursor: pointer;
    margin-bottom: 0.5rem;
    dd {
      flex-wrap: wrap;
    }
  }
`;
const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;


function AdminRegister() {
  const { adminLoginInfo } = useContext(AdminLoginInfoContext);
  const loginAuthCode = Array.isArray(adminLoginInfo.auth_code) ? adminLoginInfo.auth_code : []; // 배열로 변환
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  const [idCheck, setIdCheck] = useState(false);
  const darkAtom = useRecoilState(isDarkAtom);

  // useForm
  let { register, formState: { errors, isValid } } = useForm<any>({
    mode: 'onChange',
  });

  const [admin, setAdmin] = useState<Admin>({
    idx: 0, 
    id: '',
    name: '',
    mobile: '',
    email: '',
    auth_group: 'SA',
    approval_status: 0,
    pwd_check_date: '',
    create_date: '',
    update_date: '',
    auth_code: [],
    jisa_code: [],
  });

  const [jisaList, setJisaList] = useState<Jisa[]>([]);

  const getJisaList = useCallback(async () => {
    const response = await getJisaAll(0, 0);
    if (response.code !== 200) return;
    setJisaList(response.data);
  }, []);

  const handleChange = (event: any) => {
    const { name, value, checked } = event.target;
  
    setAdmin((prevAdmin) => {
      if (name === "auth_code" || name === "jisa_code") {
        const key = name as keyof Admin; // name을 Admin의 키로 캐스팅
        const currentValues = Array.isArray(prevAdmin[key]) ? prevAdmin[key] as string[] : [];
        const newValues = checked
          ? [...currentValues, value] // 체크되었으면 추가
          : currentValues.filter((code) => code !== value); // 체크 해제 시 제거
  
        return { ...prevAdmin, [key]: newValues };
      }
  
      if (name === "id") {
        setIdCheck(false);
        const encPassword = encryptSHA256(value);
        return {
          ...prevAdmin,
          [name]: value,
          password: encPassword, // password는 암호화된 값으로 설정
        };
      }
  
      return {
        ...prevAdmin,
        [name]: name === "approval_status" ? Number(value) : value, // approval_status는 숫자로 변환
      };
    });
  };

  function handleToNumericValue(event: any) {
    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    event.target.value = numericValue;
    handleChange(event);
  }

  async function _createAdmin(): Promise<void> {
    if (!idCheck) {
      alert('ID 중복체크 후 등록해주세요.');
      return;
    }
    const response = await createAdmin(admin);
    if (response.code === 200) {
      alert(`관리자가 생성되었습니다.`);
      navigate(`/admin/mgmt/list`);
    } else {
      alert(response.message);
    }
  }

  async function duplicateCheck(): Promise<void> {
    if (!admin.id) { alert('ID를 입력해주세요.'); return; }
    if (errors.id) { alert('영문자로 시작하는 영문자 또는 숫자 4~10자로 입력해주세요.'); return; }
    const response = await getAdminDuplicateCheck(admin.id);
    if (response.code === 200) {
      alert(`${admin.id}계정은 이미 사용중입니다.`);
      setIdCheck(false);
    } else {
      alert(`${admin.id}는 사용 가능합니다.`);
      setIdCheck(true);
    }
  }
  useEffect(() => {
    getJisaList();
  }, [getJisaList]);
  return (
    <Container>
      <TitleAndBtnContainer>
        <div>
          <p className='title-h2'>
            <span>관리자 등록</span>
          </p>
        </div>
        <div>
          <button type="button" className='default-btn edit-btn' onClick={_createAdmin} disabled={!isValid}>등록</button>
          <button type="button" className='default-btn list-btn' onClick={() => navigate(-1)}>목록</button>
        </div>
      </TitleAndBtnContainer>
      <div className={`${hubLayout.acc__cnt}`}>
        <div className={`${hubLayout.acc_item_info}`}>
          <dl>
            <dt><label>ID</label></dt>
            <dd className={`${hubLayout.display_flex}`}>
              <input type="text" value={admin.id} placeholder="ID를 입력하세요." className={`${errors.id && hubLayout.border_red}`}
                {...register("id", {
                  required: true,
                  pattern: /^[a-z][0-9a-z]{3,9}$/g,
                  onChange: handleChange
                })}
              />
              <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue} ${hubLayout.width_100}`} onClick={duplicateCheck}>중복체크</button>
            </dd>
            <dt><label>그룹</label></dt>
            <dd className={`${hubLayout.display_flex}`}>
              <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '15rem' }}>
                <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={admin.auth_group}
                  {...register(`auth_group`, {
                    required: true,
                    onChange: handleChange
                  })}>
                  <option value={`SA`}>관리자/매니저/영업자</option>
                  <option value={`A`}>관리자</option>
                  <option value={`M`}>매니저</option>
                  <option value={`S`}>영업자</option>
                </select>
              </div>
            </dd>
            <dt></dt><dd></dd>
          </dl>
          <p className={`${errors.id && hubLayout.tx_red}`} style={{ paddingLeft: '6rem' }}>* 영문자로 시작하는 영문자 또는 숫자 4~10자로 입력해주세요.</p>
          <dl>
            <dt><label>이름</label></dt>
            <dd className={`${hubLayout.display_flex}`}>
              <input type="text" value={admin.name} placeholder="이름을 입력해주세요." className={`${errors.name && hubLayout.border_red}`}
                {...register("name", {
                  required: {
                    value: true,
                    message: '이름을 입력해주세요',
                  },
                  onChange: handleChange
                })}
              />
            </dd>
            <dt><label>연락처</label></dt>
            <dd className={`${hubLayout.display_flex}`}>
              <input type="text" value={admin.mobile} placeholder="연락처를 입력해주세요." className={`${errors.mobile && hubLayout.border_red}`} maxLength={11}
                {...register("mobile", {
                  required: {
                    value: true,
                    message: '연락처를 입력해주세요',
                  },
                  pattern : {
                    value: /^01[016789]\d{3,4}\d{4}$/g,
                    message: `정확한 연락처를 입력해주세요.`,
                  },
                  onChange: (event) => {
                    handleToNumericValue(event);
                    maxLengthCheck(event);
                  }
                })}
              />
            </dd>
            <dt><label>E-MAIL</label></dt>
            <dd className={`${hubLayout.display_flex}`}>
              <input type="text" value={admin.email} placeholder="E-MAIL을 입력해주세요." className={`${errors.email && hubLayout.border_red}`}
                {...register("email", {
                  required: {
                    value: true,
                    message: 'E-MAIL을 입력해주세요',
                  },
                  onChange: handleChange
                })}
              />
            </dd>
            <dt></dt><dd></dd>
          </dl>
          <dl>
            <dt><label>승인상태</label></dt>
            <dd className={`${hubLayout.display_flex}`}>
              <span className={`${hubLayout.margin_right_10}`}>
                <input type="radio" className={`${hubLayout.radio}`} value="0" id='inp-approval-wait' checked={admin.approval_status === 0} style={{ top: '7px' }}
                  {...register(`approval_status`, {
                    required: true,
                    onChange: handleChange
                  })}
                />
                <label htmlFor='inp-approval-wait' style={{ color: darkAtom ? '#fff' : '' }}>승인대기</label>
              </span>
              <span className={`${hubLayout.margin_right_10}`}>
                <input type="radio" className={`${hubLayout.radio}`} value="1" id='inp-approval' checked={admin.approval_status === 1} style={{ top: '7px' }}
                  {...register(`approval_status`, {
                    required: true,
                    onChange: handleChange
                  })}
                />
                <label htmlFor='inp-approval' style={{ color: darkAtom ? '#fff' : '' }}>승인</label>
              </span>
              <span className={`${hubLayout.margin_right_10}`}>
                <input type="radio" className={`${hubLayout.radio}`} value="2" id='inp-not-approval' checked={admin.approval_status === 2} style={{ top: '7px' }}
                  {...register(`approval_status`, {
                    required: true,
                    onChange: handleChange
                  })}
                />
                <label htmlFor='inp-not-approval' style={{ color: darkAtom ? '#fff' : '' }}>미승인</label>
              </span>
            </dd>
          </dl>
        </div>
      </div>

      {/* 권한 */}
      {loginAuthCode.includes('F') && (
        <details>
          <summary>
            <p className='title-h2'>
              <FiInfo />
              <span>권한</span>
            </p>
          </summary>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              <dl>
                <dd className={`${hubLayout.display_flex}`}>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="A" id='inp-auth-code-A' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("A")} 
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-A' style={{ color: darkAtom ? '#fff' : '' }}>고객관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="B" id='inp-auth-code-B' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("B")} 
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-B' style={{ color: darkAtom ? '#fff' : '' }}>장비관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="C" id='inp-auth-code-C' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("C")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-C' style={{ color: darkAtom ? '#fff' : '' }}>계약관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="C01" id='inp-auth-code-C01' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("C01")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-C01' style={{ color: darkAtom ? '#fff' : '' }}>계약승인관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="D" id='inp-auth-code-D' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("D")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-D' style={{ color: darkAtom ? '#fff' : '' }}>공지사항</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="E" id='inp-auth-code-E' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("E")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-E' style={{ color: darkAtom ? '#fff' : '' }}>관리자 관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="F" id='inp-auth-code-F' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("F")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-F' style={{ color: darkAtom ? '#fff' : '' }}>관리자 권한 관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="G" id='inp-auth-code-G' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("G")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-G' style={{ color: darkAtom ? '#fff' : '' }}>A/S 관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="H" id='inp-auth-code-H' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("H")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-H' style={{ color: darkAtom ? '#fff' : '' }}>지사 관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="RM" id='inp-auth-code-RM' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("RM")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-RM' style={{ color: darkAtom ? '#fff' : '' }}>모델 관리</label>
                  </span>
                </dd>
              </dl>
            </div>
          </div>
        </details>
      )}
      {/* 관할 지사 */}
      <details>
        <summary>
          <p className='title-h2'>
            <FiInfo />
            <span>관할 지사</span>
          </p>
        </summary>
        <div className={`${hubLayout.acc__cnt}`}>
          <div className={`${hubLayout.acc_item_info}`}>
            <dl>
              <dd className={`${hubLayout.display_flex}`}>
                {jisaList.map((jisa: Jisa, idx: number) => (
                  <span key={`jisa_${idx}`} className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value={jisa.jisa_code} id={`inp-${jisa.jisa_code}`} className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.jisa_code) && admin.jisa_code.includes(jisa.jisa_code)} 
                      {...register(`jisa_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor={`inp-${jisa.jisa_code}`} style={{ color: darkAtom ? '#fff' : '' }}>{jisa.jisa_name}</label>
                  </span>
                ))}
              </dd>
            </dl>
          </div>
        </div>
      </details>
    </Container>
  );
}
export default AdminRegister;